import React from "react";
import { Box, Stack, Typography, Chip, IconButton, Tooltip } from "@mui/material";
import { motion } from "framer-motion";
import { FiPause, FiPlay, FiArchive, FiRefreshCw, FiCalendar, FiChevronRight, FiRotateCcw, FiTrash2 } from "react-icons/fi";
import dayjs from "dayjs";
import { recurrenceSummary, PRIORITY_OPTIONS, STATUS_COLORS } from "../constants/recurrenceConstants";

const T = {
  border: "#E2E8F0", text: "#0F172A", textSub: "#475569", textMuted: "#94A3B8",
  primary: "#0D9488", primaryLight: "#F0FDFA", surface: "#FFFFFF",
  shadow: { sm: "0 1px 4px rgba(15,23,42,0.07)", md: "0 4px 16px rgba(15,23,42,0.08)" },
};

const RecurringTemplateCard = ({ template, onPause, onResume, onArchive, onRestore, onDelete, onView }) => {
  const priority   = PRIORITY_OPTIONS.find((p) => p.value === template.priority);
  const statusMeta = STATUS_COLORS[template.status] || STATUS_COLORS.active;
  const summary    = recurrenceSummary(template);
  const nextRun    = template.nextRunAt ? dayjs(template.nextRunAt).format("D MMM YYYY") : "—";
  const isArchived = template.isArchived || template.status === "archived";

  return (
    <motion.div whileHover={{ y: -2 }} transition={{ duration: 0.15 }}>
      <Box
        sx={{
          bgcolor: isArchived ? "#FFFBEB" : T.surface,
          border: `1px solid ${isArchived ? "#FDE68A" : T.border}`,
          borderRadius: "14px",
          p: 2.5,
          boxShadow: T.shadow.sm,
          cursor: isArchived ? "default" : "pointer",
          transition: "box-shadow 0.2s",
          "&:hover": { boxShadow: T.shadow.md },
        }}
        onClick={isArchived ? undefined : onView}
      >
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>

          {/* Left content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.8 }} flexWrap="wrap">
              {template.templateNo && (
                <Typography sx={{ fontSize: "11px", fontWeight: 700, color: T.primary, bgcolor: "#F0FDFA", px: "6px", py: "2px", borderRadius: "5px", border: "1px solid #99F6E4", flexShrink: 0, fontFamily: "monospace" }}>
                  #{template.templateNo}
                </Typography>
              )}
              <Typography
                sx={{
                  fontSize: "14px", fontWeight: 700, color: T.text,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  maxWidth: 220,
                }}
              >
                {template.title}
              </Typography>
              <Chip
                label={isArchived ? "Archived" : statusMeta.label}
                size="small"
                sx={{
                  height: 20, fontSize: "10px", fontWeight: 700, borderRadius: "5px",
                  bgcolor: isArchived ? "#FDE68A" : statusMeta.bg,
                  color:   isArchived ? "#92400E" : statusMeta.color,
                }}
              />
            </Stack>

            <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
              <FiRefreshCw size={11} color={T.textMuted} />
              <Typography sx={{ fontSize: "12px", color: T.textSub, fontWeight: 500 }}>
                {summary}
              </Typography>
            </Stack>

            <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
              {priority && (
                <Box sx={{ px: "8px", py: "2px", borderRadius: "5px", bgcolor: priority.bg }}>
                  <Typography sx={{ fontSize: "11px", fontWeight: 700, color: priority.color }}>
                    {priority.label}
                  </Typography>
                </Box>
              )}
              {!isArchived && (
                <Stack direction="row" alignItems="center" gap="4px">
                  <FiCalendar size={11} color={T.textMuted} />
                  <Typography sx={{ fontSize: "11px", color: T.textMuted }}>Next: {nextRun}</Typography>
                </Stack>
              )}
              <Typography sx={{ fontSize: "11px", color: T.textMuted }}>
                {template.instanceCount || 0} spawned
              </Typography>
            </Stack>
          </Box>

          {/* Action buttons */}
          <Stack direction="row" alignItems="center" gap={0.5} onClick={(e) => e.stopPropagation()}>
            {isArchived ? (
              /* ── Archived state: Restore + Delete ── */
              <>
                <Tooltip title="Restore to active" arrow>
                  <IconButton
                    size="small"
                    onClick={onRestore}
                    sx={{ color: T.textMuted, "&:hover": { color: T.primary, bgcolor: T.primaryLight } }}
                  >
                    <FiRotateCcw size={14} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete" arrow>
                  <IconButton
                    size="small"
                    onClick={onDelete}
                    sx={{ color: T.textMuted, "&:hover": { color: "#DC2626", bgcolor: "#FEF2F2" } }}
                  >
                    <FiTrash2 size={14} />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              /* ── Active/Paused state: Pause|Resume + Archive ── */
              <>
                {template.status === "active" ? (
                  <Tooltip title="Pause" arrow>
                    <IconButton
                      size="small"
                      onClick={onPause}
                      sx={{ color: T.textMuted, "&:hover": { color: "#D97706", bgcolor: "#FFFBEB" } }}
                    >
                      <FiPause size={14} />
                    </IconButton>
                  </Tooltip>
                ) : template.status === "paused" ? (
                  <Tooltip title="Resume" arrow>
                    <IconButton
                      size="small"
                      onClick={onResume}
                      sx={{ color: T.textMuted, "&:hover": { color: T.primary, bgcolor: T.primaryLight } }}
                    >
                      <FiPlay size={14} />
                    </IconButton>
                  </Tooltip>
                ) : null}

                <Tooltip title="Archive" arrow>
                  <IconButton
                    size="small"
                    onClick={onArchive}
                    sx={{ color: T.textMuted, "&:hover": { color: "#D97706", bgcolor: "#FFFBEB" } }}
                  >
                    <FiArchive size={14} />
                  </IconButton>
                </Tooltip>

                <FiChevronRight size={16} color={T.textMuted} />
              </>
            )}
          </Stack>
        </Stack>
      </Box>
    </motion.div>
  );
};

export default React.memo(RecurringTemplateCard);
